import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Film, Link2, Loader2, UploadCloud, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 4 * 1024 * 1024;
const MAX_BYTES = 200 * 1024 * 1024;
const EXT_RE = /\.(mp4|mov|m4v|webm)$/i;

export default function GymUploadPage() {
  const { token } = useParams();
  const [gym, setGym] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [mode, setMode] = useState('file');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | uploading | processing | done | error
  const [error, setError] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/gym-upload/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setGym)
      .catch(() => setInvalid(true));
    return () => clearInterval(pollRef.current);
  }, [token]);

  const pickFile = (f) => {
    setError('');
    if (!f) return;
    if (!EXT_RE.test(f.name)) { setError('Please choose an .mp4, .mov, .m4v or .webm video.'); return; }
    if (f.size > MAX_BYTES) { setError('Video must be under 200MB — try trimming or exporting at 1080p.'); return; }
    setFile(f);
  };

  const startUpload = async () => {
    if (!file || phase === 'uploading') return;
    setPhase('uploading');
    setError('');
    const uploadId = (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-upload`).toLowerCase();
    const total = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < total; i += 1) {
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('index', i);
        form.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        const res = await fetch(`${API}/api/gym-upload/${token}/chunk`, { method: 'POST', body: form });
        if (!res.ok) throw new Error((await res.json()).detail || 'Chunk upload failed');
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const res = await fetch(`${API}/api/gym-upload/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id: uploadId, filename: file.name, total_chunks: total }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      setPhase('processing');
      let polls = 0;
      pollRef.current = setInterval(async () => {
        polls += 1;
        try {
          const job = await fetch(`${API}/api/gym-upload/${token}/status/${uploadId}`).then((r) => r.json());
          if (job.status === 'done') { clearInterval(pollRef.current); setPhase('done'); return; }
          if (job.status === 'failed') { clearInterval(pollRef.current); setPhase('error'); setError('Processing failed — please try a different export of your video.'); return; }
          if (polls > 120) { clearInterval(pollRef.current); setPhase(job.status === 'processing' ? 'done' : 'error'); }
        } catch {
          if (polls > 120) { clearInterval(pollRef.current); setPhase('done'); }
        }
      }, 3000);
    } catch (err) {
      setPhase('error');
      setError(err.message || 'Upload failed — please retry.');
    }
  };

  const submitLink = async () => {
    if (linkSubmitting) return;
    setLinkSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/gym-upload/${token}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_url: linkUrl }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Submission failed');
      setPhase('done');
    } catch (err) {
      setError(err.message || 'Submission failed — please retry.');
    } finally {
      setLinkSubmitting(false);
    }
  };

  if (invalid) {
    return (
      <div className="min-h-[70vh] bg-black flex items-center justify-center px-6" data-testid="gym-upload-invalid">
        <div className="text-center max-w-md">
          <XCircle className="w-10 h-10 text-[#E63946] mx-auto mb-4" />
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Link Not Recognized</h1>
          <p className="text-zinc-400 text-sm">This upload link is invalid or has expired. Reply to your NinjAthlete invite email and we'll send you a fresh one.</p>
        </div>
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="min-h-[70vh] bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-zinc-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-black px-6 pt-32 pb-14" data-testid="gym-upload-page">
      <div className="max-w-xl mx-auto">
        <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          NinjAthlete HYROX Directory
        </p>
        <h1 className="text-white text-4xl sm:text-5xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }} data-testid="gym-upload-gym-name">
          Add {gym.name}'s Video
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          Upload a short clip of your gym — a class in action, the floor, the sleds. We optimize it for web and it appears on your {gym.city ? `${gym.city} ` : ''}directory listing after a quick review.
          {gym.has_video && <span className="text-zinc-500"> Your listing already has a video — a new upload will replace it once approved.</span>}
        </p>

        {phase === 'done' ? (
          <div className="border border-[#C7FA3E]/30 bg-[#C7FA3E]/[0.05] p-8 text-center" data-testid="gym-upload-success">
            <CheckCircle2 className="w-10 h-10 text-[#C7FA3E] mx-auto mb-4" />
            <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Video Received</h2>
            <p className="text-zinc-400 text-sm">Our team will review it shortly — it'll be live on your listing within 1–2 business days.</p>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-6">
              {[['file', 'Upload a File', Film], ['link', 'Paste a YouTube Link', Link2]].map(([m, label, Icon]) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(''); }}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest border transition-colors ${mode === m ? 'border-[#C7FA3E] text-[#C7FA3E] bg-[#C7FA3E]/[0.06]' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                  data-testid={`gym-upload-mode-${m}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            {mode === 'file' ? (
              <div>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files[0]); }}
                  onClick={() => inputRef.current?.click()}
                  className={`cursor-pointer border border-dashed p-10 text-center transition-colors ${dragOver ? 'border-[#C7FA3E] bg-[#C7FA3E]/[0.04]' : 'border-zinc-700 hover:border-zinc-500'}`}
                  data-testid="gym-upload-drop"
                >
                  <UploadCloud className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
                  {file ? (
                    <p className="text-white text-sm">{file.name} <span className="text-zinc-500">({(file.size / 1024 / 1024).toFixed(1)} MB)</span></p>
                  ) : (
                    <>
                      <p className="text-zinc-300 text-sm mb-1">Drag &amp; drop your video here, or click to browse</p>
                      <p className="text-zinc-600 text-xs">.mp4, .mov or .webm · up to 200MB</p>
                    </>
                  )}
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".mp4,.mov,.m4v,.webm,video/mp4,video/quicktime,video/webm"
                    className="hidden"
                    onChange={(e) => pickFile(e.target.files[0])}
                    data-testid="gym-upload-file-input"
                  />
                </div>

                {(phase === 'uploading' || phase === 'processing') && (
                  <div className="mt-5" data-testid="gym-upload-progress">
                    <div className="h-1.5 bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-[#C7FA3E] transition-all" style={{ width: `${phase === 'processing' ? 100 : progress}%` }} />
                    </div>
                    <p className="text-zinc-400 text-xs mt-2 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {phase === 'processing' ? 'Optimizing your video for web — this can take a minute…' : `Uploading… ${progress}%`}
                    </p>
                  </div>
                )}

                {phase !== 'uploading' && phase !== 'processing' && (
                  <button
                    onClick={startUpload}
                    disabled={!file}
                    className="mt-5 w-full py-3.5 bg-[#C7FA3E] hover:bg-[#b3e635] disabled:bg-zinc-800 disabled:text-zinc-600 text-black text-xs font-bold uppercase tracking-widest transition-colors"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                    data-testid="gym-upload-submit"
                  >
                    Upload Video
                  </button>
                )}
              </div>
            ) : (
              <div>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=…"
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#C7FA3E] outline-none text-white text-sm px-4 py-3.5 placeholder:text-zinc-600"
                  data-testid="gym-upload-link-input"
                />
                <button
                  onClick={submitLink}
                  disabled={!linkUrl.trim() || linkSubmitting}
                  className="mt-4 w-full py-3.5 bg-[#C7FA3E] hover:bg-[#b3e635] disabled:bg-zinc-800 disabled:text-zinc-600 text-black text-xs font-bold uppercase tracking-widest transition-colors"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                  data-testid="gym-upload-link-submit"
                >
                  Submit Video Link
                </button>
              </div>
            )}

            {error && (
              <p className="mt-4 text-[#E63946] text-sm flex items-center gap-2" data-testid="gym-upload-error">
                <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
